/**
 * Barra vertical de servidores.
 *
 * O indicador da esquerda cresce conforme o estado: um traço curto para o
 * servidor com mensagens não lidas, um traço longo para o servidor aberto. É o
 * mesmo vocabulário visual do Discord porque já é familiar, e resolve bem o
 * problema de mostrar dois estados diferentes num espaço de quatro pixels.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Compass, Plus, Shield } from 'lucide-react'
import type { Guild } from '@nexus/shared'
import { avatarColor, initials } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { Tooltip } from '@/components/ui'
import { CreateGuildModal } from '@/components/layout/CreateGuildModal'

export function GuildRail() {
  const guilds = useAppStore((s) => s.guilds)
  const currentGuildId = useAppStore((s) => s.currentGuildId)
  const selectGuild = useAppStore((s) => s.selectGuild)
  const channels = useAppStore((s) => s.channels)
  const readStates = useAppStore((s) => s.readStates)
  const messages = useAppStore((s) => s.messages)
  const isAdmin = useAuthStore((s) => s.user?.isAdmin ?? false)

  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)

  const guildList = useMemo(
    () => Object.values(guilds).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [guilds],
  )

  /** Servidores com alguma mensagem não lida em canal de texto. */
  const unreadGuilds = useMemo(() => {
    const result = new Set<string>()
    for (const channel of Object.values(channels)) {
      if (channel.type !== 'text') continue
      const list = messages[channel.id]
      if (!list || list.length === 0) continue

      const read = readStates[channel.id]
      const last = list[list.length - 1]!
      if (!read?.lastReadMessageId || BigInt(last.id) > BigInt(read.lastReadMessageId)) {
        result.add(channel.guildId)
      }
    }
    return result
  }, [channels, messages, readStates])

  return (
    <>
      <nav
        aria-label="Servidores"
        className="flex w-rail shrink-0 flex-col items-center gap-2 bg-surface-deepest py-3"
      >
        {guildList.map((guild) => (
          <GuildButton
            key={guild.id}
            guild={guild}
            active={guild.id === currentGuildId}
            unread={unreadGuilds.has(guild.id)}
            onClick={() => selectGuild(guild.id)}
          />
        ))}

        <Tooltip content="Criar servidor" side="right">
          <button
            type="button"
            onClick={() => setCreating(true)}
            aria-label="Criar servidor"
            className={cn(
              'group flex size-12 items-center justify-center rounded-3xl bg-surface-sunken text-success',
              'transition-all duration-200 hover:rounded-2xl hover:bg-success hover:text-white',
            )}
          >
            <Plus className="size-6" />
          </button>
        </Tooltip>

        <Tooltip content="Entrar com convite" side="right">
          <button
            type="button"
            onClick={() => navigate('/entrar')}
            aria-label="Entrar com convite"
            className={cn(
              'flex size-12 items-center justify-center rounded-3xl bg-surface-sunken text-content-secondary',
              'transition-all duration-200 hover:rounded-2xl hover:bg-brand hover:text-white',
            )}
          >
            <Compass className="size-6" />
          </button>
        </Tooltip>

        {isAdmin ? (
          <>
            <div className="my-1 h-px w-8 bg-line" />
            <Tooltip content="Painel de administração" side="right">
              <button
                type="button"
                onClick={() => navigate('/admin')}
                aria-label="Painel de administração"
                className={cn(
                  'flex size-12 items-center justify-center rounded-3xl bg-surface-sunken text-warning',
                  'transition-all duration-200 hover:rounded-2xl hover:bg-warning hover:text-surface-deepest',
                )}
              >
                <Shield className="size-6" />
              </button>
            </Tooltip>
          </>
        ) : null}
      </nav>

      <CreateGuildModal open={creating} onClose={() => setCreating(false)} />
    </>
  )
}

function GuildButton({
  guild,
  active,
  unread,
  onClick,
}: {
  guild: Guild
  active: boolean
  unread: boolean
  onClick: () => void
}) {
  return (
    <div className="relative flex w-full justify-center">
      <span
        aria-hidden="true"
        className={cn(
          'absolute top-1/2 left-0 w-1 -translate-y-1/2 rounded-r-full bg-content-primary',
          'transition-all duration-200',
          active ? 'h-10' : unread ? 'h-2' : 'h-0',
        )}
      />

      <Tooltip content={guild.name} side="right">
        <button
          type="button"
          onClick={onClick}
          aria-label={guild.name}
          aria-current={active ? 'page' : undefined}
          className={cn(
            'flex size-12 items-center justify-center overflow-hidden font-semibold text-white',
            'transition-all duration-200',
            active ? 'rounded-2xl' : 'rounded-3xl hover:rounded-2xl',
          )}
          style={guild.iconUrl ? undefined : { backgroundColor: avatarColor(guild.id) }}
        >
          {guild.iconUrl ? (
            <img src={guild.iconUrl} alt="" className="size-full object-cover" />
          ) : (
            <span className="text-sm">{initials(guild.name)}</span>
          )}
        </button>
      </Tooltip>
    </div>
  )
}
